import { Button, Form } from 'antd';
import { type Moment } from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type SubmitReportTemplateProp } from '../AdvancedReportForm';
import { FormActions } from '../reportForm.style';
import { ReportParamFields } from '../ReportParamFields';
import { type DeliveryModeArgumentOption } from '../types';

import { type UserTemplateOptionFields, getExtraParamFields } from './utils';

export type SubmitUserTemplateOptionProps = {
  reportTemplates: SubmitReportTemplateProp[];
  deliveryModeId?: string;
  hermesId?: string;
  shareInHorizonOrganizationId?: string;
  dateFilter?: string;
  weatherStandbyRate?: string;
  siteStandbyRate?: string;
  dailyBillableDelayHours?: string;
};

type Props = {
  option: UserTemplateOptionFields;
  shareInHorizonOrganizations?: DeliveryModeArgumentOption[];
  onSubmit: (props: SubmitUserTemplateOptionProps) => void;
  onCancel: () => void;
};

export const AdditionalReportParamsForm = ({
  option,
  shareInHorizonOrganizations = [],
  onSubmit,
  onCancel,
}: Props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const fields = getExtraParamFields(option);
  const [isValidHermesId, setIsValidHermesId] = useState(false);

  const handleFinish = ({
    hermesId,
    shareInHorizonOrganizationId,
    dateFilter,
    weatherStandbyRate,
    siteStandbyRate,
    dailyBillableDelayHours,
  }: {
    hermesId?: string;
    shareInHorizonOrganizationId?: string;
    dateFilter?: Moment;
    weatherStandbyRate?: number;
    siteStandbyRate?: number;
    dailyBillableDelayHours?: number;
  }) => {
    onSubmit({
      reportTemplates: [{ id: option.template.id, variantId: option.variant?.id }],
      deliveryModeId: option.deliveryMode?.id,
      hermesId,
      shareInHorizonOrganizationId,
      dateFilter: dateFilter?.format('YYYY-MM-DD'),
      weatherStandbyRate: weatherStandbyRate?.toString(),
      siteStandbyRate: siteStandbyRate?.toString(),
      dailyBillableDelayHours: dailyBillableDelayHours?.toString(),
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      onValuesChange={(changedValues) => {
        if (Object.prototype.hasOwnProperty.call(changedValues, 'hermesId')) {
          setIsValidHermesId(Boolean(changedValues.hermesId));
        }
      }}
      initialValues={{
        dailyBillableDelayHours: 8,
        shareInHorizonOrganizationId: shareInHorizonOrganizations[0]?.id,
      }}
    >
      <ReportParamFields
        showHermesId={fields.hermesId}
        showShareInHorizonOrganization={fields.shareInHorizonOrganization}
        showCostRates={fields.costRates}
        showDateFilter={fields.dateFilter}
        shareInHorizonOrganizations={shareInHorizonOrganizations}
      />
      <FormActions>
        <Button
          type="primary"
          htmlType="submit"
          disabled={fields.hermesId ? !isValidHermesId : false}
        >
          {t('common.confirm', 'Confirm')}
        </Button>
        <Button onClick={onCancel}>{t('common.cancel', 'Cancel')}</Button>
      </FormActions>
    </Form>
  );
};
